import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import { tinFormat } from "../../../../../utils/stringUtils";
import {
  getEmploymentStatuses,
  saveAccountSpouseInfoAsync,
} from "../../../../../services/accountService";
import Textbox from "../../../../../components/common/Textbox";
import TextboxMask from "../../../../../components/common/TextboxMask";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Toaster from "./../../../../../components/common/Toaster";

const schema = yup.object({
  first_name: yup
    .string()
    .matches("^[a-zA-ZñÑ][a-zA-ZñÑ .-]*$", "Please enter a valid name."),
  middle_name: yup
    .string()
    .matches("^[a-zA-ZñÑ][a-zA-ZñÑ .-]*$", "Please enter a valid name."),
  last_name: yup
    .string()
    .matches("^[a-zA-ZñÑ][a-zA-ZñÑ .-]*$", "Please enter a valid name."),
  employment_status: yup.object().shape({
    label: yup.string().required("Please select employment type."),
    value: yup.string().required("Please select employment type."),
  }),
});

const SpouseForm = ({ id, data, isLoading, setLoading, onSuccess }) => {
  const [status, setStatus] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await saveAccountSpouseInfoAsync(id, data);
      Toaster.Success("Information Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const result = getEmploymentStatuses();
    setStatus(result);
  }, []);

  useEffect(() => {
    const populateForm = () => {
      setValue("first_name", data.first_name);
      setValue("middle_name", data.middle_name);
      setValue("last_name", data.last_name);
      setValue("employer_name", data.employer_name);
      setValue(
        "taxpayer_identification_number",
        data.taxpayer_identification_number
      );

      if (data.employment_status) {
        const selected_status = {
          label: data.employment_status_type,
          value: data.employment_status,
        };

        setValue("employment_status", selected_status);
      }
    };

    if (data) populateForm();
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="first-name-textbox"
            name="first_name"
            type="text"
            title="First Name"
            placeholder="e.g. Juan"
            register={register("first_name")}
            errors={errors}
            autoFocus
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="middle-name-textbox"
            name="middle_name"
            type="text"
            title="Middle Name"
            register={register("middle_name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="last-name-textbox"
            name="last_name"
            type="text"
            title="Last Name"
            placeholder="e.g. Dela Cruz"
            register={register("last_name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="employment-status-dropdown"
            name="employment_status"
            title="Employment Status"
            options={status}
            control={control}
            register={register("employment_status")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="employer-name-textbox"
            name="employer_name"
            type="text"
            title="Employer Name"
            register={register("employer_name")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <TextboxMask
            id="tin-textbox"
            name="taxpayer_identification_number"
            type="tel"
            title="Taxpayer Identification No."
            format={tinFormat}
            setValue={setValue}
            value={data && data.taxpayer_identification_number}
            register={register("taxpayer_identification_number")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default SpouseForm;
